// First-party, privacy-conscious website analytics.
// Stores only anonymous identifiers. No IPs, no fingerprinting, no 3rd-party trackers.
import api, { API } from "./api";

const VID_KEY = "top_vid";              // localStorage: anonymous visitor id (persistent)
const SID_KEY = "top_sid";              // sessionStorage: session id
const SID_LAST = "top_sid_last";        // sessionStorage: last-activity epoch (ms)
const ATTR_KEY = "top_attr";            // sessionStorage: campaign/referrer attribution for the session
const EXCLUDE_KEY = "top_exclude";      // localStorage: "1" to exclude this browser (admin/testing)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getVisitorId() {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) { v = uuid(); localStorage.setItem(VID_KEY, v); }
    return v;
  } catch { return ""; }
}

function getSession() {
  try {
    const now = Date.now();
    const last = parseInt(sessionStorage.getItem(SID_LAST) || "0", 10);
    let sid = sessionStorage.getItem(SID_KEY);
    let isNew = false;
    if (!sid || !last || now - last > SESSION_TIMEOUT_MS) {
      sid = uuid();
      isNew = true;
      sessionStorage.setItem(SID_KEY, sid);
    }
    sessionStorage.setItem(SID_LAST, String(now));
    return { sid, isNew };
  } catch { return { sid: "", isNew: false }; }
}

// Capture UTM + referrer once per session (first touch wins).
function getAttribution() {
  try {
    const existing = sessionStorage.getItem(ATTR_KEY);
    if (existing) return JSON.parse(existing);
    const p = new URLSearchParams(window.location.search);
    const attr = {
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
      utm_content: p.get("utm_content") || "",
      utm_term: p.get("utm_term") || "",
      referrer: document.referrer || "",
    };
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    return { utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "", utm_term: "", referrer: "" };
  }
}

export function isExcluded() {
  try { return localStorage.getItem(EXCLUDE_KEY) === "1"; } catch { return false; }
}
export function setExcluded(on) {
  try { on ? localStorage.setItem(EXCLUDE_KEY, "1") : localStorage.removeItem(EXCLUDE_KEY); } catch { /* noop */ }
}

// Headers used to attribute a completed conversion (order/inquiry) to a source/campaign.
export function analyticsHeaders() {
  const attr = getAttribution();
  return {
    "X-Analytics-Vid": getVisitorId(),
    "X-Analytics-Sid": getSession().sid,
    "X-Analytics-Source": attr.utm_source,
    "X-Analytics-Medium": attr.utm_medium,
    "X-Analytics-Campaign": attr.utm_campaign,
    "X-Analytics-Referrer": attr.referrer,
    ...(isExcluded() ? { "X-Analytics-Exclude": "1" } : {}),
  };
}

// Attach anonymous attribution headers to every API request (used for conversion attribution).
let _attached = false;
export function attachConversionHeaders() {
  if (_attached) return;
  _attached = true;
  api.interceptors.request.use((cfg) => {
    try { cfg.headers = { ...(cfg.headers || {}), ...analyticsHeaders() }; } catch { /* noop */ }
    return cfg;
  });
}

export function trackPageview(path) {
  if (isExcluded()) return;
  if (path && path.startsWith("/admin")) return;
  try {
    const attr = getAttribution();
    const { sid, isNew } = getSession();
    const body = JSON.stringify({
      path, visitor_id: getVisitorId(), session_id: sid, new_session: isNew, ...attr,
    });
    // Lightweight, async, never blocks the page or throws.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/track`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/track`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true, credentials: "include" }).catch(() => {});
    }
  } catch { /* analytics must never break the site */ }
}
